import { ACCOUNTING_NAV, accountingHref } from '@/components/accounting/nav';
import type { MyPermissions } from '@/lib/use-my-permissions';

/**
 * The org sidebar's modules, in Figma order (node 2167:24977), plus the rule
 * that decides which of them a principal may see.
 *
 * This lives apart from `org-sidebar.tsx` because it now has a second reader.
 * ✅ E04-C11 (BA/Bao Tran, 2026-07-15) says there is **no fixed role→redirect
 * table**: what a user lands on follows the permissions granted to them (E03).
 * The sidebar already obeyed that (US-13 AC2 / BR-E03-19) while the post-login
 * landing did not, so an invited user was dropped on `/users` — a screen their
 * own sidebar was hiding from them. Both now read this one list, so the first
 * module in the nav and the first module they land on cannot drift apart.
 *
 * `key` doubles as the icon filename in /public/sidebar/<key>.svg — the real
 * Hugeicons vectors exported straight from the Figma org-sidebar. Only shipped
 * routes get an href; the rest stay visible but disabled until their epic lands.
 *
 * `moduleKey` ties a nav item to a `module_catalog` row so the FE hides it when
 * `GET /me/permissions` reports no `can_view` for the current principal. Items
 * with no `moduleKey` are always shown; access to the screens themselves stays
 * enforced server-side. Some modules expand into an in-sidebar submenu (e.g.
 * Accounting): `groupBase` marks the item active for any path under it, and
 * `children` are the submenu rows.
 */
export type SubNavItem = { key: string; label: string; href: string };

export type NavItem = {
  key: string;
  label: string;
  href?: string;
  moduleKey?: string;
  /**
   * Extra `module_catalog` prefix that also opens the item. Accounting is gated
   * per feature (`accounting.invoice`, `accounting.journal`, ... - E15 seeds 19
   * keys), while this nav row predates E15 and carries the single legacy key
   * `accountings`. A user granted a real accounting feature but not the legacy
   * key would otherwise hold permissions with no way to reach the screens.
   */
  moduleKeyPrefix?: string;
  groupBase?: string;
  /** Submenu rows. An item that has them is a click-to-open dropdown. */
  children?: SubNavItem[];
};

// `moduleKey` values are the E03-C01 confirmed 13 module_catalog keys
// (product_family/variation/bundle/offers/mail_plans/stock/purchase_order/
// customer/orders/accountings/notification/users/company). Product Family and
// Variation, and Bundle and Offers, are distinct modules per spec.
// WEST-624 (user 2026-09-16): Inventory now carries its OWN key, `inventory`.
// It is a page in the sidebar, so an Org Admin must be able to grant and revoke
// it; while it borrowed `variation` the permission matrix had no Inventory row
// at all (the matrix only draws catalog keys a MAIN_NAV row owns), and the page
// came and went with Product Variation. Migration 0136 creates the catalog row
// (0015 had deleted the one 0005 seeded) with `{view}` as its only action, and
// backfills `inventory.can_view` for everyone who holds `variation.can_view`,
// so no live user loses the page on the day this ships.
//
// ⚠ STILL TRUE, AND THE REASON THE KEY IS `inventory` AND NOT `stock`:
// `menuModuleOf` awards a catalog key to the FIRST MAIN_NAV row that owns it,
// and Inventory sits BEFORE Stock. `moduleKey:'stock'` here would relabel the
// permission matrix's Stock section "Inventory" and move a stock-only user's
// landing from /stock to /inventory. `inventory-nav-restored.test.tsx` pins
// both.
//
// The screen's own reads are `GET /product-variations` (`variation:view`) and
// `GET /offers` (`offers:view`), which is why `VIEW_IMPLIED_BY`
// (`packages/contracts/src/permission-policy.ts`) opens those two reads off an
// Inventory grant — without it a user granted Inventory alone would land on a
// screen that 403s on everything it fetches.
export const MAIN_NAV: NavItem[] = [
  // WEST-793 (US-41B): the organization Dashboard over every Mail Plan, first in
  // the menu. It carries its OWN key, `dashboard`, for the reason Inventory does
  // (WEST-624, see above): `menuModuleOf` awards a catalog key to the FIRST row
  // that owns it, so borrowing `mail_plans` here would relabel the permission
  // matrix's Mail Plan section "Dashboard". The migration that creates the key
  // carries every `mail_plans.can_view` grant over, so nobody loses the page.
  // Being first also makes it the landing screen of whoever holds it.
  { key: 'dashboard', label: 'Dashboard', href: '/dashboard', moduleKey: 'dashboard' },
  {
    key: 'product-family',
    label: 'Product Family',
    href: '/products',
    moduleKey: 'product_family',
  },
  {
    key: 'product-variation',
    label: 'Product Variation',
    href: '/product-variations',
    moduleKey: 'variation',
  },
  { key: 'bundles', label: 'Bundles', href: '/bundles', moduleKey: 'bundle' },
  { key: 'offers', label: 'Offers', href: '/offers', moduleKey: 'offers' },
  { key: 'mail-plan', label: 'Mail Plan', href: '/mail-plans', moduleKey: 'mail_plans' },
  // Restored 2026-08-19 (user decision, BA feedback 18/08 item 4), superseding
  // the 2026-08-05 removal. It came out because `components/inventory/data.ts`
  // shipped 18 invented products, the same fake catalog for every tenant, and
  // the note blamed E08. The blame was wrong: E08 is Offer Chain & Retention and
  // this screen never read from it. The catalog is a composition over E06
  // variations and E07 offers, both shipped, and the data layer now reads them.
  { key: 'inventory', label: 'Inventory', href: '/inventory', moduleKey: 'inventory' },
  { key: 'stock', label: 'Stock', href: '/stock', moduleKey: 'stock' },
  {
    key: 'purchase-order',
    label: 'Purchase Order',
    href: '/purchase-orders',
    moduleKey: 'purchase_order',
  },
  { key: 'customers', label: 'Customers', href: '/customers', moduleKey: 'customer' },
  { key: 'orders', label: 'Orders', href: '/orders', moduleKey: 'orders' },
  {
    key: 'accounting',
    label: 'Accounting',
    href: '/accounting/reports',
    moduleKey: 'accountings',
    moduleKeyPrefix: 'accounting.',
    groupBase: '/accounting',
    // Opens in place on click (user 2026-09-22), like Advanced Routing, instead
    // of expanding only while the route is already under /accounting. Route-led
    // expansion could never animate: each page mounts its own sidebar, so the
    // submenu arrived already open and the user got a jump, not a transition.
    // `href` stays: it is not the row's click target any more, but
    // `firstPermittedLanding` reads it, so an accounting-only principal still
    // lands on /accounting/reports after login.
    children: ACCOUNTING_NAV.map((s) => ({
      key: `accounting-${s.slug}`,
      label: s.label,
      href: accountingHref(s.slug),
    })),
  },
  // Notifications carries NO `moduleKey` on purpose, so it is one of the rows
  // `canViewNavItem` always shows. BA 2026-08-13 on the Create/Edit User form:
  // "Notification: khong can bo vao, User nao cung co" - every user has
  // notifications, so the permission matrix stopped offering the `notification`
  // key. Gating this row on a key nobody can be granted any more would hide the
  // screen from every org user, the opposite of what BA asked for.
  // The BE agrees: `NotificationPreferencesController` and
  // `ApprovalNotificationController` are both listed in `NO_PERMISSION_GATE`
  // (own rows only), so an always-visible row cannot land on a 403.
  { key: 'notifications', label: 'Notifications', href: '/notifications' },
];

export const SETTINGS_NAV: NavItem[] = [
  { key: 'setting', label: 'Setting', href: '/users', moduleKey: 'users' },
  { key: 'company', label: 'Company', href: '/company', moduleKey: 'company' },
  {
    key: 'advanced-routing',
    label: 'Advanced Routing',
    moduleKey: 'integrations',
    children: [
      { key: 'call-centers', label: 'Call Centers', href: '/call-centers' },
      { key: 'merchant-accounts', label: 'Merchant Accounts', href: '/merchant-accounts' },
      { key: 'payment-routing', label: 'Payment Routing', href: '/payment-routing' },
    ],
  },
];

/**
 * A module is visible only when the effective set grants `can_view`. Until the
 * set loads (or if it fails), `can()` answers true so the nav never flashes
 * empty — server-side PermissionGuard still blocks any disallowed action.
 */
export function canViewNavItem(item: NavItem, permissions: MyPermissions): boolean {
  if (!item.moduleKey) return true;
  if (permissions.can(item.moduleKey, 'view')) return true;
  // Per-feature keys count too, so an accounting grant alone reveals the module.
  return item.moduleKeyPrefix
    ? permissions.rows.some(
        (row) => row.can_view && row.module_key.startsWith(item.moduleKeyPrefix as string),
      )
    : false;
}

/**
 * The first screen this principal may actually open, or null while that is not
 * knowable yet (⚠ E04-C11: landing follows permission, not role).
 *
 * Null on purpose in two cases, and the caller must not paper over either:
 *  - the set has not loaded. `can()` fails open before that, which is right for
 *    the nav (never flash empty) and wrong here — it would send everyone to
 *    whatever the first module happens to be. The caller holds the frame.
 *  - the set loaded and grants nothing AND no ungated row has an href. There is
 *    then no screen to land on, so the caller falls back to its persona default
 *    rather than inventing one. Since BA 2026-08-13 the Notifications row is
 *    ungated, so a granted-nothing user now lands there: the one screen their
 *    own sidebar shows them, instead of a screen it hides.
 */
export function firstPermittedLanding(permissions: MyPermissions): string | null {
  if (!permissions.ready) return null;
  for (const item of [...MAIN_NAV, ...SETTINGS_NAV]) {
    if (item.href && canViewNavItem(item, permissions)) return item.href;
  }
  return null;
}
